import { useState, useCallback, useEffect, useMemo } from "react";
import { MantineThemeProvider } from "@mantine/core";
import { useForm } from "react-hook-form";
import { useFormSubmit } from "./hooks/useFormSubmit";
import { TURNSTILE_CONFIG } from "@/config/apiConfig";
import { useDynamicFormConfig } from "./hooks/useDynamicFormConfig";
import { useDynamicValidationResolver } from "./hooks/useDynamicValidationResolver";
import { usePhotoValidation } from "./hooks/usePhotoValidation";
import { usePrivacyConsent } from "./hooks/usePrivacyConsent";
import { ensureHttpsRedirectUrl, usePostSubmitRedirect } from "./hooks/usePostSubmitRedirect";
import { useHiddenFieldsReset } from "./hooks/useHiddenFieldsReset";
import { buildSubmissionPayload } from "./utils/buildSubmissionPayload";
import { buildFieldsTheme } from "./utils/buildFieldsTheme";
import { DynamicFormShell } from "./sections/DynamicFormShell";
import { DynamicFormSuccess } from "./sections/DynamicFormSuccess";
import { DynamicFormForm } from "./sections/DynamicFormForm";

export const DynamicForm = ({ config, siteConfig, layout, hasSiteHeader = false, formGuid }) => {
    const [selectedGender, setSelectedGender] = useState(null);
    const [turnstileToken, setTurnstileToken] = useState(TURNSTILE_CONFIG.enabled ? null : "xxx");
    const [validationSummary, setValidationSummary] = useState([]);
    const { submit, loading, error, success } = useFormSubmit(formGuid);

    const { images, imageErrors, validatePhotos, handleImagesChange, resetPhotos } = usePhotoValidation(config.photos || []);
    const {
        privacyConsent,
        privacyError,
        validatePrivacyConsent,
        handlePrivacyConsentChange,
        resetPrivacyConsent,
    } = usePrivacyConsent();

    const { genderField, countryField, hasCountryPhoneSync, visibleFields, logoUrl } = useDynamicFormConfig({
        config,
        siteConfig,
        selectedGender,
        // Whether the header is actually shown is decided once in App, after the
        // URL has been resolved and validated: re-deriving it from the raw
        // configuration here would disagree with it.
        hideLogo: hasSiteHeader && layout.header.hideFormLogo,
    });
    const { dynamicResolver } = useDynamicValidationResolver(visibleFields);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        control,
        setValue,
        getValues,
    } = useForm({
        resolver: dynamicResolver,
        mode: "onBlur",
    });

    const watchedGender = genderField ? watch(genderField.name) : null;
    useEffect(() => {
        if (watchedGender !== selectedGender) {
            setSelectedGender(watchedGender);
        }
    }, [watchedGender, selectedGender]);

    const watchedCountry = hasCountryPhoneSync && countryField ? watch(countryField.name) : null;

    const redirectUrl = ensureHttpsRedirectUrl(siteConfig?.redirectUrl);
    // Absent or non-boolean values keep the thank-you message. Without a URL there is nowhere to go.
    const skipThanksMessage = siteConfig?.skipThanksMessage === true && Boolean(redirectUrl);

    usePostSubmitRedirect(success, redirectUrl, skipThanksMessage);
    useHiddenFieldsReset({
        selectedGender,
        fields: config.fields,
        getValues,
        setValue,
    });

    const onSubmit = useCallback(
        async (data) => {
            const payload = buildSubmissionPayload({
                fields: config.fields,
                data,
                selectedGender,
                images,
                turnstileToken,
                privacyConsent,
            });

            await submit(payload);
        },
        [config.fields, selectedGender, images, turnstileToken, privacyConsent, submit]
    );

    const scrollToValidationAlert = useCallback(() => {
        const alertNode = document.getElementById("form-validation-alert");
        if (alertNode) {
            alertNode.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, []);

    const handleFormSubmit = useCallback(
        (event) => {
            const photosValid = validatePhotos();
            const privacyValid = validatePrivacyConsent();
            setValidationSummary([]);

            handleSubmit((data) => {
                if (photosValid && privacyValid) {
                    onSubmit(data);
                    return;
                }

                const extraErrors = [];
                if (!photosValid) {
                    extraErrors.push("Required photo uploads");
                }
                if (!privacyValid) {
                    extraErrors.push("Privacy consent");
                }
                setValidationSummary(extraErrors);
                scrollToValidationAlert();
            }, (invalidErrors) => {
                const invalidFieldNames = Object.keys(invalidErrors || {});
                const invalidFieldLabels = invalidFieldNames.map((fieldName) => {
                    const matchingField = visibleFields.find((field) => field.name === fieldName);
                    return matchingField?.label || fieldName;
                });

                if (!photosValid) {
                    invalidFieldLabels.push("Required photo uploads");
                }
                if (!privacyValid) {
                    invalidFieldLabels.push("Privacy consent");
                }

                setValidationSummary(Array.from(new Set(invalidFieldLabels)));
                scrollToValidationAlert();
            })(event);
        },
        [validatePhotos, validatePrivacyConsent, handleSubmit, onSubmit, privacyConsent, scrollToValidationAlert, turnstileToken, visibleFields]
    );

    const handleReset = useCallback(() => {
        reset();
        setSelectedGender(null);
        resetPhotos();
        resetPrivacyConsent();
        setTurnstileToken(TURNSTILE_CONFIG.enabled ? null : "xxx");
    }, [reset, resetPhotos, resetPrivacyConsent]);

    const canSubmit = Boolean(privacyConsent && turnstileToken);

    // Applies the configured field style to every input below, without touching each field.
    const fieldsTheme = useMemo(() => buildFieldsTheme(layout.fields), [layout.fields]);

    return (
        <MantineThemeProvider theme={fieldsTheme}>
            <DynamicFormShell
                logoUrl={logoUrl}
                title={config.title}
                description={config.description}
                error={error}
                validationSummary={validationSummary}
                layout={layout}
            >
                {success && !skipThanksMessage ? (
                    <DynamicFormSuccess redirectUrl={redirectUrl} />
                ) : (
                    <DynamicFormForm
                        onSubmit={handleFormSubmit}
                        visibleFields={visibleFields}
                        register={register}
                        control={control}
                        errors={errors}
                        watch={watch}
                        setValue={setValue}
                        countryCode={hasCountryPhoneSync ? watchedCountry : undefined}
                        photos={config.photos}
                        onImagesChange={handleImagesChange}
                        imageErrors={imageErrors}
                        privacyConsent={privacyConsent}
                        onPrivacyConsentChange={handlePrivacyConsentChange}
                        privacyError={privacyError}
                        siteConfig={siteConfig}
                        onTurnstileSuccess={setTurnstileToken}
                        onTurnstileError={() => setTurnstileToken(null)}
                        onTurnstileExpire={() => setTurnstileToken(null)}
                        loading={loading}
                        onReset={handleReset}
                        canSubmit={canSubmit}
                        layout={layout}
                    />
                )}
            </DynamicFormShell>
        </MantineThemeProvider>
    );
};
